// src/pages/tenant/TenantSettingsPage.tsx
import React, { useState } from 'react';
import {
  Box, Typography, Paper, Stack, Button, TextField, Alert,
  Divider, CircularProgress, InputAdornment, IconButton,
  useTheme, useMediaQuery, Tabs, Tab,
} from '@mui/material';
import {
  Settings as SettingsIcon, Lock as LockIcon,
  Visibility, VisibilityOff, Save as SaveIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

const TenantSettingsPage: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useAuth();

  const [tab, setTab] = useState(0);

  // ── Profile state ──────────────────────────────────────────────────────────
  const [profile, setProfile] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
  });
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [profileSuccess, setProfileSuccess] = useState('');

  // ── Password state ─────────────────────────────────────────────────────────
  const [passwords, setPasswords] = useState({ current: '', newPass: '', confirm: '' });
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  const surface = isDark ? 'rgba(255,255,255,0.03)' : '#ffffff';
  const border  = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';
  const subtext = isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)';
  const headingColor = isDark ? '#ffffff' : '#0f172a';

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2, fontSize: '0.875rem',
      background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
      '& fieldset': { borderColor: border },
      '&:hover fieldset': { borderColor: '#34d399' },
      '&.Mui-focused fieldset': { borderColor: '#34d399' },
    },
    '& .MuiInputLabel-root.Mui-focused': { color: '#34d399' },
  };

  const handleProfileSave = async () => {
    setProfileError('');
    setProfileSuccess('');
    if (!profile.firstName.trim() || !profile.lastName.trim()) {
      setProfileError('First and last name are required');
      return;
    }
    setProfileLoading(true);
    try {
      await api.put('/auth/profile', {
        firstName: profile.firstName.trim(),
        lastName: profile.lastName.trim(),
      });
      setProfileSuccess('Profile updated successfully');
    } catch (e: any) {
      setProfileError(e?.response?.data?.message || 'Failed to update profile');
    } finally {
      setProfileLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    setPasswordError('');
    setPasswordSuccess('');

    if (!passwords.current || !passwords.newPass || !passwords.confirm) {
      setPasswordError('All password fields are required');
      return;
    }
    if (passwords.newPass.length < 8) {
      setPasswordError('New password must be at least 8 characters');
      return;
    }
    if (passwords.newPass !== passwords.confirm) {
      setPasswordError('New passwords do not match');
      return;
    }
    if (passwords.current === passwords.newPass) {
      setPasswordError('New password must be different from current password');
      return;
    }

    setPasswordLoading(true);
    try {
      await api.post('/auth/change-password', {
        currentPassword: passwords.current,
        newPassword: passwords.newPass,
      });
      setPasswordSuccess('Password changed successfully');
      setPasswords({ current: '', newPass: '', confirm: '' });
    } catch (e: any) {
      setPasswordError(e?.response?.data?.message || 'Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <Box>
      {/* ── Header ── */}
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 3 }}>
        <Box sx={{ width: 36, height: 36, borderRadius: 2, background: 'rgba(52,211,153,0.12)', display: 'grid', placeItems: 'center' }}>
          <SettingsIcon sx={{ color: '#34d399', fontSize: 20 }} />
        </Box>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 900, lineHeight: 1.2, color: headingColor }}>Settings</Typography>
          <Typography variant="body2" sx={{ color: subtext, fontWeight: 500 }}>Manage your account and preferences</Typography>
        </Box>
      </Stack>

      {/* ── Tabs ── */}
      <Box sx={{ borderBottom: `1px solid ${border}`, mb: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}
          sx={{ '& .MuiTab-root': { textTransform: 'none', fontWeight: 700, fontSize: '0.875rem' }, '& .MuiTabs-indicator': { backgroundColor: '#34d399' } }}>
          <Tab label="Profile" icon={<PersonIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
          <Tab label="Password" icon={<LockIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
        </Tabs>
      </Box>

      {/* ── Profile Tab ── */}
      {tab === 0 && (
        <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${border}`, background: surface, p: 3, maxWidth: 560 }}>
          <Typography sx={{ fontWeight: 900, fontSize: '0.9rem', color: headingColor, mb: 2 }}>
            Personal Information
          </Typography>

          {profileError   && <Alert severity="error"   onClose={() => setProfileError('')}   sx={{ mb: 2, borderRadius: 2 }}>{profileError}</Alert>}
          {profileSuccess && <Alert severity="success" onClose={() => setProfileSuccess('')} sx={{ mb: 2, borderRadius: 2 }}>{profileSuccess}</Alert>}

          <Stack spacing={2}>
            <Stack direction={isMobile ? 'column' : 'row'} spacing={2}>
              <TextField fullWidth label="First Name" value={profile.firstName}
                onChange={e => setProfile(p => ({ ...p, firstName: e.target.value }))}
                size="small" sx={fieldSx} />
              <TextField fullWidth label="Last Name" value={profile.lastName}
                onChange={e => setProfile(p => ({ ...p, lastName: e.target.value }))}
                size="small" sx={fieldSx} />
            </Stack>

            <TextField fullWidth label="Email Address" value={profile.email}
              size="small" sx={fieldSx} disabled
              helperText="Email cannot be changed. Contact support if needed."
            />

            <Divider sx={{ borderColor: border }} />

            <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)', border: `1px solid ${border}` }}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: subtext, textTransform: 'uppercase', letterSpacing: '0.07em', mb: 0.5 }}>Role</Typography>
              <Box sx={{ display: 'inline-flex', px: 1.25, py: 0.35, borderRadius: 1.5, bgcolor: 'rgba(248,113,113,0.12)' }}>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: '#f87171', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                  {user?.role?.replace(/_/g, ' ')}
                </Typography>
              </Box>
            </Box>

            <Button variant="contained" onClick={handleProfileSave} disabled={profileLoading}
              startIcon={profileLoading ? <CircularProgress size={16} /> : <SaveIcon />}
              sx={{ alignSelf: 'flex-start', borderRadius: 2, textTransform: 'none', fontWeight: 800, background: '#34d399', color: '#000', '&:hover': { background: '#2ecc71' } }}>
              {profileLoading ? 'Saving...' : 'Save Changes'}
            </Button>
          </Stack>
        </Paper>
      )}

      {/* ── Password Tab ── */}
      {tab === 1 && (
        <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${border}`, background: surface, p: 3, maxWidth: 480 }}>
          <Typography sx={{ fontWeight: 900, fontSize: '0.9rem', color: headingColor, mb: 0.5 }}>
            Change Password
          </Typography>
          <Typography sx={{ fontSize: '0.78rem', color: subtext, mb: 2 }}>
            Use a strong password with at least 8 characters
          </Typography>

          {passwordError   && <Alert severity="error"   onClose={() => setPasswordError('')}   sx={{ mb: 2, borderRadius: 2 }}>{passwordError}</Alert>}
          {passwordSuccess && <Alert severity="success" onClose={() => setPasswordSuccess('')} sx={{ mb: 2, borderRadius: 2 }}>{passwordSuccess}</Alert>}

          <Stack spacing={2}>
            {[
              { label: 'Current Password', value: passwords.current, show: showCurrent, toggle: () => setShowCurrent(v => !v), key: 'current' as const },
              { label: 'New Password', value: passwords.newPass, show: showNew, toggle: () => setShowNew(v => !v), key: 'newPass' as const, helper: 'At least 8 characters' },
              { label: 'Confirm New Password', value: passwords.confirm, show: showConfirm, toggle: () => setShowConfirm(v => !v), key: 'confirm' as const,
                error: !!passwords.confirm && passwords.confirm !== passwords.newPass,
                helperError: passwords.confirm && passwords.confirm !== passwords.newPass ? 'Passwords do not match' : '' },
            ].map(f => (
              <TextField key={f.key} fullWidth label={f.label}
                type={f.show ? 'text' : 'password'}
                value={f.value}
                onChange={e => setPasswords(p => ({ ...p, [f.key]: e.target.value }))}
                size="small" sx={fieldSx}
                error={!!(f as any).error}
                helperText={(f as any).helperError || (f as any).helper || ''}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><LockIcon sx={{ fontSize: 18, color: subtext }} /></InputAdornment>,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={f.toggle} edge="end" size="small">
                        {f.show ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            ))}

            <Divider sx={{ borderColor: border }} />

            <Button variant="contained" onClick={handlePasswordChange} disabled={passwordLoading}
              startIcon={passwordLoading ? <CircularProgress size={16} /> : <LockIcon />}
              sx={{ alignSelf: 'flex-start', borderRadius: 2, textTransform: 'none', fontWeight: 800, background: '#34d399', color: '#000', '&:hover': { background: '#2ecc71' } }}>
              {passwordLoading ? 'Updating...' : 'Update Password'}
            </Button>
          </Stack>
        </Paper>
      )}
    </Box>
  );
};

export default TenantSettingsPage;